import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";

import { getCommunity } from "../../config/communityConfig.jsx";

import CommunityHero from "../../components/community/CommunityHero.jsx";
import CommunityHighlights from "../../components/community/CommunityHighlights.jsx";
import CommunityProducts from "../../components/community/CommunityProducts.jsx";
import { COMMUNITY_MASK } from "../../config/communityAccess.js";
import ProductQuickView from "../../components/ProductQuickView.jsx";

import { useCartContext } from "../../components/CartContext";

import {
    getCommunityHighlights,
    getCommunityProducts,
} from "../../api/communityApi.js";

export default function CommunityHomePage() {

    const navigate = useNavigate();

    const { category } = useParams();

    const community = getCommunity(category);

    const [loading, setLoading] = useState(true);

    const [highlights, setHighlights] = useState([]);

    const [products, setProducts] = useState([]);

    const [selectedProduct, setSelectedProduct] = useState(null);
    const [selectedVariant, setSelectedVariant] = useState(null);
    const [quantity, setQuantity] = useState(1);
    const [images, setImages] = useState([]);

    const { addItem } = useCartContext();

    useEffect(() => {

        loadCommunity();

    }, [category]);

    async function loadCommunity() {

        try {

            setLoading(true);

            const [highlightsResult, productsResult] = await Promise.allSettled([
                getCommunityHighlights(category),
                getCommunityProducts(COMMUNITY_MASK[category] || 0)
            ]);

            if (highlightsResult.status === "fulfilled") {
                setHighlights(highlightsResult.value);
            } else {
                console.error("Highlights API failed:", highlightsResult.reason);
                setHighlights([]);
            }

            if (productsResult.status === "fulfilled") {
                console.log("Products:", productsResult.value);
                setProducts(productsResult.value);
            } else {
                console.error("Products API failed:", productsResult.reason);
                setProducts([]);
            }

        } catch (err) {

            console.error("Community Load Error", err);

        } finally {

            setLoading(false);

        }

    }

    if (!community) {

        return (

            <div className="min-h-screen flex items-center justify-center">

                Community not found

            </div>

        );

    }

    return (

        <div
            className="
                min-h-screen
            "
        >

            {/* Decorative Background */}

            <div
                className="
                    fixed
                    inset-0
                    -z-10
                    overflow-hidden
                "
            >

                <div
                    className="
                        absolute
                        top-20
                        right-10
                        w-96
                        h-96
                        rounded-full
                        blur-[120px]
                        opacity-20
                    "
                    style={{
                        background: community.accent
                    }}
                />

                <div
                    className="
                        absolute
                        bottom-10
                        left-10
                        w-80
                        h-80
                        rounded-full
                        blur-[100px]
                        opacity-20
                    "
                    style={{
                        background: community.accent
                    }}
                />

            </div>

            {/* Page */}

            <div className="max-w-7xl mx-auto px-6 pb-24">

                {/* Back */}

                <button

                    onClick={() => navigate("/")}

                    className="
                        mt-10
                        mb-8
                        flex
                        items-center
                        gap-2
                        text-gray-600
                        hover:text-gray-900
                        transition
                    "

                >

                    <ArrowLeft size={18} />

                    Back to all Kalps

                </button>

                {/* HERO */}

                <CommunityHero

                    community={community}

                    onExploreProducts={() => {

                        navigate(

                            `/shop?community=${community.shopCollection}`

                        );

                    }}

                    onJoinCommunity={() => {

                        navigate(

                            `/community/${category}/threads`

                        );

                    }}

                />

                {/* Highlights */}

                <CommunityHighlights

                    community={community}

                    highlights={highlights}

                    onOpenThread={(thread) => {

                        navigate(

                            `/community/${category}/threads/${thread.id}`

                        );

                    }}

                />

                {/* Products */}

                <CommunityProducts

                    community={community}

                    products={products}

                    onWishlist={(product) => {

                        console.log(product);

                    }}

                    onProductClick={(product) => {

                        setSelectedProduct(product);

                        setSelectedVariant(product.variants?.[0] || null);

                        setImages(
                            product.variants?.[0]?.imageUrls || []
                        );

                        setQuantity(1);

                    }}

                    onShopCollection={() => {

                        navigate(

                            `/shop?community=${community.shopCollection}`

                        );

                    }}

                />

                {/* CTA */}

                <motion.div

                    initial={{

                        opacity: 0,

                        y: 40

                    }}

                    whileInView={{

                        opacity: 1,

                        y: 0

                    }}

                    viewport={{

                        once: true

                    }}

                    className="
                        mt-28
                        rounded-[40px]
                        overflow-hidden
                        bg-gradient-to-r
                        from-slate-900
                        via-slate-800
                        to-slate-900
                        text-white
                        p-16
                    "

                >

                    <div
                        className="
                            max-w-4xl
                            mx-auto
                            text-center
                        "
                    >

                        <div
                            className="
                                text-5xl
                                font-black
                            "
                        >

                            Become part of

                            <br />

                            {community.title}

                        </div>

                        <p
                            className="
                                mt-6
                                text-lg
                                text-white/80
                                leading-8
                            "
                        >

                            Join thousands of creators,
                            builders and dreamers who are
                            already sharing ideas every day.

                        </p>

                        <div
                            className="
                                mt-10
                                flex
                                justify-center
                                gap-5
                                flex-wrap
                            "
                        >

                            <button

                                onClick={() => {

                                    navigate(

                                        `/community/${category}/threads`

                                    );

                                }}

                                className={`
                                    px-8
                                    py-4
                                    rounded-full
                                    text-white
                                    font-semibold
                                    ${community.button}
                                `}

                            >

                                Enter Community

                            </button>

                            <button

                                onClick={() => {

                                    navigate(

                                        `/shop?community=${community.shopCollection}`

                                    );

                                }}

                                className="
                                    px-8
                                    py-4
                                    rounded-full
                                    border
                                    border-white/20
                                    bg-white/10
                                    backdrop-blur-xl
                                "

                            >

                                Shop Collection

                            </button>

                        </div>

                    </div>

                </motion.div>

            </div>

            {selectedProduct && (

                <ProductQuickView

                    product={selectedProduct}

                    variants={selectedProduct.variants || []}

                    images={images}

                    selectedVariantId={selectedVariant?.id}

                    onVariantSelect={(variant) => {

                        setSelectedVariant(variant);

                        setImages(variant.imageUrls || []);

                    }}

                    quantity={quantity}

                    onInc={() => setQuantity(q => q + 1)}

                    onDec={() => setQuantity(q => Math.max(1, q - 1))}

                    onClose={() => {

                        setSelectedProduct(null);

                        setSelectedVariant(null);

                        setImages([]);

                    }}

                    onAdd={(item) => {

                        addItem(item);

                    }}

                />

            )}

        </div>

    );

}